// ════════════════════════════════════════════════════════════════════════════
// TableOrderPage — "QR en mesa": the customer scans the table's QR and lands
// here (/t/:locationId/:tableId). Digital menu → cart → the order lands as an
// OPEN TAB for the table (pay at the register) — or APPENDS to the tab staff
// already opened on a handheld (recovery gated by the tab's guest number, so
// a confused scan at the wrong table can't hijack someone else's order).
// ════════════════════════════════════════════════════════════════════════════
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFirestore, collection, getDocs, doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { STRIPE_PUBLISHABLE_KEY } from '../firebaseConfig';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const money = (v) => `$${(Number(v) || 0).toFixed(2)}`;

const TableOrderPageInner = () => {
  const { locationId, tableId } = useParams();
  const navigate = useNavigate();
  const db = getFirestore();
  const functions = getFunctions();
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ctx, setCtx] = useState(null); // getTableContext result
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selCat, setSelCat] = useState('all');
  // Cart + recovered-tab session persist per table (survive reloads) — the
  // user's #4 question: yes, localStorage is the right tool here.
  const cartKey = `qrCart_${locationId}_${tableId}`;
  const tabKey = `qrTab_${locationId}_${tableId}`;
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(cartKey) || '[]'); } catch (_) { return []; }
  }); // {productId, productName, price, qty, instructions, options:[labels], optionPicks:[{optionId, choice}]}
  const [productOptions, setProductOptions] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [detailProduct, setDetailProduct] = useState(null); // product in the options modal
  const [selOptions, setSelOptions] = useState({}); // optionId → [choiceNames]
  const [detailQty, setDetailQty] = useState(1);
  const [detailInstructions, setDetailInstructions] = useState('');
  const [geoNote, setGeoNote] = useState(null);

  // Recovery flow
  const [showRecover, setShowRecover] = useState(false);
  const [guestInput, setGuestInput] = useState('');
  const [recovering, setRecovering] = useState(false);
  const [recoverError, setRecoverError] = useState('');
  const [tabRecovered, setTabRecovered] = useState(() => {
    try { const t = JSON.parse(localStorage.getItem(tabKey) || 'null'); return t?.recoveryToken ? t : null; } catch (_) { return null; }
  }); // verifyTableTab result

  // Order flow
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(null); // placeTableOrder result

  // Payment + lost-order recovery
  const [payMethod, setPayMethod] = useState('register'); // 'register' | 'card'
  const [tipSel, setTipSel] = useState(''); // percent string or custom $
  const [cardError, setCardError] = useState('');
  const [showLost, setShowLost] = useState(false);
  const [lostContact, setLostContact] = useState('');
  const [lostSent, setLostSent] = useState(false);
  const [lostBusy, setLostBusy] = useState(false);

  // ── Load: QR context + published menu ──────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const getCtx = httpsCallable(functions, 'getTableContext');
        const rt = new URLSearchParams(window.location.search).get('rt');
        const { data } = await getCtx({ locationId, tableId, ...(rt ? { rt } : {}) });
        if (!data.success) throw new Error('Invalid table QR');
        setCtx(data);

        // Published menu (publicStores catalog — same source as the store page)
        const catSnap = await getDocs(collection(db, 'publicStores', locationId, 'categories'));
        const cats = [];
        const prods = [];
        for (const catDoc of catSnap.docs) {
          cats.push({ id: catDoc.id, ...catDoc.data() });
          const pSnap = await getDocs(collection(db, 'publicStores', locationId, 'categories', catDoc.id, 'products'));
          pSnap.docs.forEach((p) => {
            const pd = p.data();
            if (pd.available === false) return;
            prods.push({ id: p.id, categoryId: catDoc.id, ...pd });
          });
        }
        cats.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setCategories(cats);
        setProducts(prods);

        // Product options (for products that need a choice / instructions)
        try {
          const optSnap = await getDocs(collection(db, 'publicStores', locationId, 'productOptions'));
          setProductOptions(optSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        } catch (e) { /* options optional */ }

        if (data.recovered) {
          const rec = {
            recoveryToken: data.recovered.recoveryToken,
            guestNumber: data.recovered.guestNumber,
            tabName: `Order #${data.recovered.guestNumber}`,
            items: data.recovered.items || [],
            total: data.recovered.total || 0,
          };
          setTabRecovered(rec);
          try { localStorage.setItem(tabKey, JSON.stringify(rec)); } catch (_) {}
          // consume the magic token from the URL
          window.history.replaceState({}, '', window.location.pathname);
        } else if (data.openTab) setShowRecover(true);
        setLoading(false);

        // Live tab listener: if staff closes the table's tab from the POS,
        // drop the recovered session so the page never acts on a closed tab.
        if (data.openTab?.tabId) {
          const tRef = doc(db, 'users', data.businessId, 'openTabs', data.openTab.tabId);
          onSnapshot(tRef, (snap) => {
            const st = snap.data()?.status;
            if (snap.exists && st && st !== 'open') {
              setTabRecovered((cur) => {
                if (cur) {
                  try { localStorage.removeItem(tabKey); } catch (_) {}
                }
                return null;
              });
            }
          });
        }

        // Soft geofence AFTER first paint (the iOS location prompt froze the
        // perceived load when fired during the initial effect) — never blocks.
        if (navigator.geolocation && data.locationLat != null && data.locationLng != null) {
          setTimeout(() => {
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                const d = _metersBetween(pos.coords.latitude, pos.coords.longitude, data.locationLat, data.locationLng);
                if (d > (data.geofenceMeters || 150)) {
                  setGeoNote(`Heads up — your GPS shows you're not near ${data.locationName}. Make sure you scanned the right table's QR.`);
                }
              },
              () => {},
              { timeout: 4000 }
            );
          }, 1500);
        }
      } catch (e) {
        setError(e.message || 'This QR code is not valid anymore. Please ask the staff.');
        setLoading(false);
      }
    })();
  }, [locationId, tableId]);

  function _metersBetween(lat1, lng1, lat2, lng2) {
    const R = 6371000;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  const visible = useMemo(() => products.filter((p) => selCat === 'all' ? true : selCat === '__deals__' ? p.onSale === true : p.categoryId === selCat), [products, selCat]);
  const cartTotal = cart.reduce((a, i) => a + i.price * i.qty, 0);
  const cartTax = cartTotal * ((ctx?.taxRate || 0) / 100);

  const needsDetail = (p) => (Array.isArray(p.optionIds) && p.optionIds.length > 0) || p.acceptSpecialRequirement === true;

  const addToCart = (p) => {
    if (needsDetail(p)) {
      setDetailProduct(p);
      setSelOptions({});
      setDetailQty(1);
      setDetailInstructions('');
      return;
    }
    const price = p.onSale === true && p.salePrice != null ? Number(p.salePrice) : Number(p.price) || 0;
    setCart((prev) => {
      const existing = prev.find((i) => i.productId === p.id && (i.options || []).length === 0 && !i.instructions);
      if (existing) return prev.map((i) => (i.productId === p.id ? { ...i, qty: i.qty + 1 } : i));
      return [...prev, { productId: p.id, productName: p.name, price, qty: 1, instructions: '', options: [], optionPicks: [] }];
    });
  };

  // Add from the detail modal (options + instructions + qty)
  const addDetailedToCart = () => {
    const p = detailProduct;
    if (!p) return;
    // required options must have a choice
    const groups = productOptions.filter((o) => (p.optionIds || []).includes(o.id));
    for (const g of groups) {
      if (g.optionRequired === true && !(selOptions[g.id] || []).length) {
        alert(`Please pick: ${g.name}`);
        return;
      }
    }
    let unit = p.onSale === true && p.salePrice != null ? Number(p.salePrice) : Number(p.price) || 0;
    const labels = [];
    const picks = [];
    for (const g of groups) {
      for (const choiceName of (selOptions[g.id] || [])) {
        const def = (g.options || []).find((o) => (o.name || o) === choiceName);
        const fee = Number(def?.extraFee || 0);
        unit += fee;
        labels.push(choiceName);
        picks.push({ optionId: g.id, choice: choiceName });
      }
    }
    setCart((prev) => [...prev, {
      productId: p.id,
      productName: p.name,
      price: unit,
      qty: detailQty,
      instructions: detailInstructions.trim(),
      options: labels,
      optionPicks: picks,
    }]);
    setDetailProduct(null);
  };
  const lineKey = (i) => `${i.productId}::${(i.options || []).join('|')}::${i.instructions || ''}`;
  const changeQty = (key, delta) => {
    setCart((prev) => prev.map((i) => (lineKey(i) === key ? { ...i, qty: Math.max(0, i.qty + delta) } : i)).filter((i) => i.qty > 0));
  };

  useEffect(() => { try { localStorage.setItem(cartKey, JSON.stringify(cart)); } catch (_) {} }, [cart]);

  // ── Recover the table's open tab (guest-number knowledge check) ───────────
  const handleRecover = async () => {
    setRecovering(true);
    setRecoverError('');
    try {
      const fn = httpsCallable(functions, 'verifyTableTab');
      const { data } = await fn({ locationId, tableId, guestNumber: guestInput.trim() });
      if (!data.success) throw new Error('Could not verify');
      setTabRecovered(data);
      try { localStorage.setItem(tabKey, JSON.stringify(data)); } catch (_) {}
      setShowRecover(false);
    } catch (e) {
      setRecoverError(e.message || 'That number does not match. Ask the staff for the number on your receipt.');
    } finally {
      setRecovering(false);
    }
  };

  // ── Lost-order recovery (magic link by SMS/email) ─────────────────────────
  const handleLostOrder = async () => {
    if (!lostContact.trim()) return;
    setLostBusy(true);
    try {
      const fn = httpsCallable(functions, 'requestTableOrderRecovery');
      await fn({ locationId, contact: lostContact.trim() });
      setLostSent(true);
    } catch (e) {
      alert(e.message || 'Could not send the link. Ask the staff.');
    } finally {
      setLostBusy(false);
    }
  };

  // ── Pay the (recovered) tab online: card / Apple Pay / Google Pay ──────────
  const handlePayOnline = async (walletEvent = null) => {
    const recovered = tabRecovered;
    if (!recovered) return;
    setPlacing(true);
    setCardError('');
    try {
      const tipDollars = tipSel === 'custom' ? (parseFloat(document.getElementById('qrCustomTip')?.value) || 0) : (parseFloat(tipSel) || 0);

      let pmId = null;
      if (walletEvent) {
        const { error: submitError } = await elements.submit();
        if (submitError) throw submitError;
        const { error: pmError, paymentMethod: pm } = await stripe.createPaymentMethod({ elements });
        if (pmError || !pm) throw (pmError || new Error('Could not read the wallet card'));
        pmId = pm.id;
      } else {
        const cardEl = elements.getElement(CardElement);
        const { error: pmError, paymentMethod: pm } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardEl,
          billing_details: { name: customerName || 'Guest' },
        });
        if (pmError) throw pmError;
        pmId = pm.id;
      }

      const fn = httpsCallable(functions, 'payTableTab');
      let { data } = await fn({
        locationId, tableId,
        recoveryToken: recovered.recoveryToken,
        tipDollars,
        paymentMethodId: pmId,
      });
      if (data.status === 'requires_action') {
        const { paymentIntent: handled, error: actionError } = await stripe.handleCardAction(data.clientSecret);
        if (actionError) throw actionError;
        if (handled.status !== 'succeeded') throw new Error(`Payment status: ${handled.status}`);
        // second leg: finalize the tab after the challenge
        const again = await fn({ locationId, tableId, recoveryToken: recovered.recoveryToken, tipDollars, paymentIntentId: data.paymentIntentId });
        data = again.data;
      }
      if (!data.success) throw new Error('Payment failed');
      setPlaced({ mode: 'paid', guestNumber: data.guestNumber, total: data.totalCharged });
      setCart([]);
    } catch (e) {
      setCardError(e.message || 'Payment failed');
    } finally {
      setPlacing(false);
    }
  };

  // ── Place the order ────────────────────────────────────────────────────────
  const handlePlaceOrder = async () => {
    if (!customerName.trim()) { setError(null); alert('Please add a name for the order'); return; }
    setPlacing(true);
    try {
      const fn = httpsCallable(functions, 'placeTableOrder');
      const { data } = await fn({
        locationId,
        tableId,
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim(),
        items: cart.map((i) => ({ productId: i.productId, qty: i.qty, instructions: i.instructions || null, options: i.options || [], optionPicks: i.optionPicks || [] })),
        ...(tabRecovered ? { recoveryToken: tabRecovered.recoveryToken } : {}),
      });
      if (!data.success) throw new Error('Could not place the order');
      setPlaced(data);
      setCart([]);
      try { localStorage.removeItem(cartKey); } catch (_) {}
    } catch (e) {
      alert(e.message || 'Could not place the order. Please ask the staff.');
    } finally {
      setPlacing(false);
    }
  };

  // ── Renders ────────────────────────────────────────────────────────────────
  if (loading) {
    return <div style={styles.center}><div className="spinner-border" style={{ color: '#ff5722' }}></div><p style={{ marginTop: 12, color: '#777' }}>Loading the menu…</p></div>;
  }
  if (error) {
    return <div style={styles.center}><i className="fas fa-exclamation-triangle fa-2x" style={{ color: '#dc2626' }}></i><p style={{ marginTop: 12, color: '#555', maxWidth: 320, textAlign: 'center' }}>{error}</p></div>;
  }

  // Order placed — confirmation screen
  if (placed) {
    return (
      <div style={{ ...styles.page, alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
        <div style={{ width: 76, height: 76, borderRadius: '50%', background: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className="fas fa-check" style={{ color: '#fff', fontSize: 34 }}></i>
        </div>
        <h2 style={{ marginTop: 16, fontWeight: 800 }}>Order sent to the kitchen</h2>
        <p style={{ color: '#666' }}>{ctx.locationName} · {ctx.tableName}</p>
        {placed.guestNumber && (
          <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 12, padding: '12px 20px', marginTop: 8 }}>
            <div style={{ fontSize: 12, color: '#9a3412', fontWeight: 600 }}>YOUR NUMBER — keep it to add more items later</div>
            <div style={{ fontSize: 36, fontWeight: 800, color: '#ea580c', letterSpacing: 2 }}>#{placed.guestNumber}</div>
          </div>
        )}
        <p style={{ marginTop: 12, color: '#777', fontSize: 14 }}>{placed.mode === 'paid'
          ? `Paid ${money(placed.total)} — thank you!`
          : `${placed.mode === 'appended' ? 'Added to your existing order' : 'New order started'} · ${money(placed.mode === 'appended' ? placed.addedTotal : placed.total)} · pay at the register when you're ready.`}</p>
        <button style={styles.primaryBtn} onClick={() => setPlaced(null)}>Order more</button>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800 }}>{ctx.locationName}</div>
          <div style={{ fontSize: 13, color: '#777' }}>{ctx.tableName}{tabRecovered ? ` · Adding to order #${tabRecovered.guestNumber}` : ''}</div>
        </div>
        <i className="fas fa-utensils" style={{ color: '#ff5722', fontSize: 22 }}></i>
      </div>

      <div style={{ textAlign: 'right', marginBottom: 6 }}>
        <button style={{ background: 'none', border: 'none', color: '#ff5722', fontSize: 13, fontWeight: 600 }} onClick={() => setShowLost(true)}>
          Lost your order link?
        </button>
      </div>

      {geoNote && (
        <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 12px', fontSize: 13, color: '#92400e', marginBottom: 10 }}>
          <i className="fas fa-location-dot" style={{ marginRight: 6 }}></i>{geoNote}
        </div>
      )}

      {/* Recovered tab banner + pay-online card */}
      {tabRecovered && (
        <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 12, padding: 12, marginBottom: 10 }}>
          <div style={{ fontWeight: 700, color: '#047857', fontSize: 14 }}>Your open order — {tabRecovered.tabName}</div>
          <div style={{ fontSize: 12, color: '#065f46', marginTop: 4 }}>
            {tabRecovered.items.map((i) => `${i.qty}× ${i.productName}`).join(' · ')} — {money(tabRecovered.total)} so far
          </div>

          {/* Pay from the phone (kiosk parity: card online + wallets) */}
          <div style={{ marginTop: 12, borderTop: '1px solid #d1fae5', paddingTop: 10 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              {[0, 10, 15, 20].map((pct) => (
                <button key={pct}
                  style={{ flex: 1, border: '1px solid #a7f3d0', background: tipSel === String(pct) ? '#059669' : '#fff', color: tipSel === String(pct) ? '#fff' : '#047857', borderRadius: 8, padding: '6px 0', fontSize: 12, fontWeight: 700 }}
                  onClick={() => setTipSel(String(pct))}>
                  {pct === 0 ? 'No tip' : `${pct}%`}
                </button>
              ))}
            </div>
            <ExpressCheckoutElement
              onConfirm={(e) => handlePayOnline(e)}
              options={{
                buttonType: { applePay: 'pay', googlePay: 'pay' },
                paymentMethods: { applePay: 'always', googlePay: 'always', link: 'never', paypal: 'never', amazonPay: 'never' },
              }}
            />
            <div style={{ margin: '10px 0 6px', fontSize: 12, fontWeight: 600, color: '#555' }}>or pay with card</div>
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 10 }}>
              <CardElement options={{ style: { base: { fontSize: '15px' } } }} />
            </div>
            {cardError && <div className="text-danger mt-2" style={{ fontSize: 13 }}>{cardError}</div>}
            <button
              style={{ ...styles.primaryBtn, background: '#059669' }}
              disabled={placing}
              onClick={() => handlePayOnline(null)}>
              {placing ? 'Processing…' : `Pay ${money(tabRecovered.total * (1 + (parseFloat(tipSel) || 0) / 100))} now`}
            </button>
          </div>
        </div>
      )}

      {/* Categories */}
      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8, marginBottom: 8 }}>
        <button style={selCat === 'all' ? styles.catChipOn : styles.catChip} onClick={() => setSelCat('all')}>All</button>
        <button style={selCat === '__deals__' ? styles.dealChipOn : styles.dealChip} onClick={() => setSelCat('__deals__')}>Deals</button>
        {categories.map((c) => (
          <button key={c.id} style={selCat === c.id ? styles.catChipOn : styles.catChip} onClick={() => setSelCat(c.id)}>{c.name}</button>
        ))}
      </div>

      {/* Products */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {visible.map((p) => {
          const price = p.onSale === true && p.salePrice != null ? Number(p.salePrice) : Number(p.price) || 0;
          return (
            <div key={p.id} style={styles.prodCard} onClick={() => addToCart(p)}>
              {p.imageUrl
                ? <img src={p.imageUrl} alt="" style={{ width: 56, height: 56, borderRadius: 10, objectFit: 'cover' }} />
                : <div style={{ width: 56, height: 56, borderRadius: 10, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><i className="fas fa-utensils" style={{ color: '#bbb' }}></i></div>}
              <div style={{ flex: 1, marginLeft: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{p.name}</div>
                {!!p.description && <div style={{ fontSize: 12, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>{p.description}</div>}
                <div style={{ fontWeight: 700, color: '#ff5722', marginTop: 2 }}>
                  {p.onSale === true && p.salePrice != null && <span style={{ textDecoration: 'line-through', color: '#aaa', fontWeight: 400, marginRight: 6, fontSize: 12 }}>{money(p.price)}</span>}
                  {money(price)}
                </div>
              </div>
              <div style={styles.addBtn}><i className="fas fa-plus"></i></div>
            </div>
          );
        })}
        <div style={{ height: 120 }}></div>
      </div>

      {/* Cart bar */}
      {cart.length > 0 && (
        <div style={styles.cartBar}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800 }}>{cart.reduce((a, i) => a + i.qty, 0)} items · {money(cartTotal + cartTax)}</div>
            <div style={{ fontSize: 12, color: '#bbb' }}>incl. tax · pay at the register</div>
          </div>
          <button style={styles.reviewBtn} onClick={() => navigate(`/t/${locationId}/${tableId}/checkout`)}>Review &amp; send</button>
        </div>
      )}

      {/* Product detail modal (options + instructions + qty) */}
      {detailProduct && (
        <div style={styles.overlay} onClick={() => setDetailProduct(null)}>
          <div style={{ ...styles.sheet, maxHeight: '85vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 style={{ fontWeight: 800, margin: 0 }}>{detailProduct.name}</h4>
              <button style={{ background: 'none', border: 'none', fontSize: 22, color: '#999' }} onClick={() => setDetailProduct(null)}>×</button>
            </div>
            {productOptions.filter((o) => (detailProduct.optionIds || []).includes(o.id)).map((g) => {
              const multi = g.allowMultipleSelection === true;
              const selected = selOptions[g.id] || [];
              return (
                <div key={g.id} style={{ marginTop: 14, textAlign: 'left' }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>
                    {g.name} {g.optionRequired === true && <span style={{ color: '#dc2626' }}>*</span>}
                    <span style={{ fontWeight: 400, color: '#999', fontSize: 12 }}> — {multi ? 'pick any' : 'pick one'}</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                    {(g.options || []).map((choice) => {
                      const cname = choice.name || choice;
                      const fee = Number(choice.extraFee || 0);
                      const isSel = selected.includes(cname);
                      return (
                        <button key={cname}
                          style={{ border: isSel ? '2px solid #ff5722' : '1px solid #ddd', background: isSel ? '#fff3ee' : '#fff', borderRadius: 20, padding: '6px 14px', fontSize: 13, fontWeight: 600 }}
                          onClick={() => setSelOptions((prev) => {
                            const cur = prev[g.id] || [];
                            if (multi) {
                              return { ...prev, [g.id]: isSel ? cur.filter((c) => c !== cname) : [...cur, cname] };
                            }
                            return { ...prev, [g.id]: [cname] };
                          })}>
                          {cname}{fee > 0 ? ` +${money(fee)}` : ''}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {detailProduct.acceptSpecialRequirement === true && (
              <div style={{ marginTop: 14, textAlign: 'left' }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>Special instructions</div>
                <textarea className="form-control" style={{ marginTop: 6 }} rows={2} placeholder="e.g. no onion, extra crispy…" value={detailInstructions} onChange={(e) => setDetailInstructions(e.target.value)} />
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 16 }}>
              <button className="btn btn-light rounded-circle" style={{ width: 38, height: 38 }} onClick={() => setDetailQty(Math.max(1, detailQty - 1))}><i className="fas fa-minus"></i></button>
              <span style={{ fontSize: 20, fontWeight: 800 }}>{detailQty}</span>
              <button className="btn btn-light rounded-circle" style={{ width: 38, height: 38 }} onClick={() => setDetailQty(detailQty + 1)}><i className="fas fa-plus"></i></button>
            </div>
            <button style={styles.primaryBtn} onClick={addDetailedToCart}>Add to order</button>
          </div>
        </div>
      )}

      {/* Open-tab recovery modal (shown on arrival when the table has a tab) */}
      {showRecover && ctx.openTab && (
        <div style={styles.overlay}>
          <div style={styles.sheet}>
            <h4 style={{ fontWeight: 800, marginBottom: 6 }}>This table already has an open order</h4>
            <p style={{ color: '#666', fontSize: 14 }}>
              {ctx.openTab.itemCount} item{ctx.openTab.itemCount === 1 ? '' : 's'} · {money(ctx.openTab.total)} so far.
              Is that you? Enter the 4-digit number the staff gave you to add to it — or start a separate order.
            </p>
            <input
              className="form-control text-center"
              style={{ fontSize: 24, fontWeight: 800, letterSpacing: 6, marginBottom: 8 }}
              maxLength={6}
              inputMode="numeric"
              placeholder="0000"
              value={guestInput}
              onChange={(e) => setGuestInput(e.target.value.replace(/[^\d]/g, ''))}
            />
            {recoverError && <div className="text-danger" style={{ fontSize: 13, marginBottom: 8 }}>{recoverError}</div>}
            <button style={styles.primaryBtn} disabled={recovering || guestInput.length < 3} onClick={handleRecover}>
              {recovering ? 'Verifying…' : 'Recover my order'}
            </button>
            <button style={styles.ghostBtn} onClick={() => setShowRecover(false)}>Start a separate order</button>
          </div>
        </div>
      )}
    </div>
  );
};



const styles = {
  dealChip: { border: '1px solid #fecaca', background: '#fff', borderRadius: 20, padding: '6px 14px', fontSize: 13, fontWeight: 700, color: '#dc2626', whiteSpace: 'nowrap' },
  dealChipOn: { border: '1px solid #dc2626', background: '#dc2626', borderRadius: 20, padding: '6px 14px', fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' },
  page: { minHeight: '100vh', background: '#f8f9fa', display: 'flex', flexDirection: 'column', padding: '16px', maxWidth: 560, margin: '0 auto', fontFamily: "'Poppins', sans-serif" },
  center: { minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: "'Poppins', sans-serif" },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', borderRadius: 16, padding: '14px 16px', marginBottom: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' },
  catChip: { border: '1px solid #e5e7eb', background: '#fff', borderRadius: 20, padding: '6px 14px', fontSize: 13, fontWeight: 600, color: '#555', whiteSpace: 'nowrap' },
  catChipOn: { border: '1px solid #ff5722', background: '#ff5722', borderRadius: 20, padding: '6px 14px', fontSize: 13, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap' },
  prodCard: { display: 'flex', alignItems: 'center', background: '#fff', borderRadius: 14, padding: 12, marginBottom: 10, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  addBtn: { width: 34, height: 34, borderRadius: '50%', background: '#ff5722', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  cartBar: { position: 'sticky', bottom: 12, display: 'flex', alignItems: 'center', background: '#1f2937', color: '#fff', borderRadius: 16, padding: '12px 16px', boxShadow: '0 8px 24px rgba(0,0,0,0.25)' },
  reviewBtn: { background: '#ff5722', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 18px', fontWeight: 700 },
  primaryBtn: { width: '100%', background: '#ff5722', color: '#fff', border: 'none', borderRadius: 12, padding: '12px', fontWeight: 700, marginTop: 10 },
  ghostBtn: { width: '100%', background: 'transparent', color: '#555', border: '1px solid #ddd', borderRadius: 12, padding: '12px', fontWeight: 600, marginTop: 8 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1060, padding: 16 },
  sheet: { background: '#fff', borderRadius: 20, padding: 24, width: '100%', maxWidth: 420 },
};

const TableOrderPage = () => (
  <Elements stripe={stripePromise}>
    <TableOrderPageInner />
  </Elements>
);

export default TableOrderPage;
